<template>
    <div>
        <breadcrumbs />
        <v-card variant="outlined">
            <v-card-title>
                <v-btn
                    v-if="$route.path.includes('registration-requests')"
                    size="small"
                    icon="mdi-arrow-left"
                    data-cy="go-to-table-btn"
                    :to="{name: 'registrationRequests'}"
                />
                {{ pageTitle }}
            </v-card-title>
            <v-card-text>
                <v-row v-if="!result">
                    <v-col cols="12">
                        <v-form ref="form" v-model="valid" :disabled="!!companyInfoMessage">
                            <v-row>
                                <v-col cols="12" md="12" class="pt-0 pb-0">
                                    <v-text-field
                                        v-model="model.name.en"
                                        :label="`${$t('company_name')}*`"
                                        :rules="[requiredRule('company_name')]"
                                        required
                                    />
                                </v-col>
                                <v-col cols="12" md="6" class="pt-0">
                                    <v-autocomplete
                                        ref="option"
                                        v-model="model.countryId"
                                        :items="countries"
                                        item-title="name"
                                        closable-chips
                                        item-value="value"
                                        hide-details
                                        :label="`${$t('country')}*`"
                                        :rules="[requiredRule('country')]"
                                        required
                                        @update:model-value="loadCities"
                                    />
                                </v-col>
                                <v-col cols="12" md="6" class="pt-0">
                                    <v-autocomplete
                                        ref="option"
                                        v-model="model.cityId"
                                        :items="cities"
                                        item-title="name"
                                        :loading="citiesLoading"
                                        closable-chips
                                        item-value="id"
                                        hide-details
                                        :label="`${$t('city')}`"
                                    />
                                </v-col>
                                <v-col cols="12" md="9" class="pb-0">
                                    <v-text-field
                                        v-model="model.street.en"
                                        :label="`${$t('address')}*`"
                                        :rules="[requiredRule('address')]"
                                        required
                                    />
                                </v-col>
                                <v-col cols="12" md="3" class="pb-0">
                                    <v-text-field v-model="model.postalCode" :placeholder="`${$t('postal_code')}`" />
                                </v-col>
                                <v-col cols="12" md="3" class="pt-0 pb-0">
                                    <v-select
                                        v-model="model.contactPerson.prefix"
                                        :items="['Mr', 'Ms', 'Mrs']"
                                        :label="`${$t('salutation')}`"
                                    />
                                </v-col>
                                <v-col cols="12" md="4" class="pt-0 pb-0">
                                    <v-text-field
                                        v-model="model.contactPerson.firstName.en"
                                        :placeholder="`${$t('first_name')}*`"
                                        :rules="validationRules('first_name', [latinRule, requiredRule])"
                                        required
                                    />
                                </v-col>
                                <v-col cols="12" md="5" class="pt-0 pb-0">
                                    <v-text-field
                                        v-model="model.contactPerson.lastName.en"
                                        :placeholder="`${$t('last_name')}*`"
                                        :rules="validationRules('last_name', [latinRule, requiredRule])"
                                        required
                                    />
                                </v-col>
                                <v-col cols="12" md="6" class="pt-0">
                                    <phone-field
                                        v-model="model.phones"
                                        :placeholder="$t('phone')"
                                        :disabled="$refs.form?.disabled"
                                        required
                                    />
                                </v-col>
                                <v-col cols="12" md="6" class="pt-0">
                                    <v-text-field
                                        v-model="model.emails"
                                        :placeholder="`${$t('email')}*`"
                                        type="email"
                                        :rules="validationRules('email', [emailRule, requiredRule])"
                                        required
                                    />
                                </v-col>
                                <v-col cols="12" md="12">
                                    <v-textarea
                                        v-model="model.comment.en"
                                        variant="outlined"
                                        :placeholder="`${$t('comment')}`"
                                        rows="3"
                                    />
                                </v-col>
                            </v-row>

                            <v-btn
                                v-if="!companyInfoMessage"
                                :loading="isLoading"
                                :disabled="isLoading || !valid"
                                color="primary"
                                block
                                @click="register"
                            >
                                {{ $t('register') }}
                            </v-btn>
                            <v-btn v-else :disabled="$refs.form?.disabled" color="primary" block>
                                {{ $t('edit') }}
                            </v-btn>
                        </v-form>
                    </v-col>
                </v-row>
                <div v-if="result">
                    {{ $t('company_registration_success') }}
                </div>
            </v-card-text>
        </v-card>
    </div>
</template>

<script lang="ts">
    import {Component, mixins} from 'vue-facing-decorator'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import RegistrationForm from '~src/components/account/forms/registrationForm.src'
    import {FormValidationRulesMixin} from '~/mixins/FormValidationRulesMixin.vue'
    import PhoneField from '@/components/snippets/forms/PhoneField.vue'
    import {clean} from '~/utils/helpers'

    @Component({
        components: {
            PhoneField,
            Breadcrumbs,
            RegistrationForm,
        },
    })
    export class CompanyRegistrationPage extends mixins(FormValidationRulesMixin) {
        valid = false
        isLoading = false
        result = false
        citiesLoading = false
        model = {
            type: 'INTERNAL_SUPPLIER',
            name: {
                en: '',
            },
            countryId: null,
            cityId: null,
            contactPerson: {
                prefix: '',
                firstName: {
                    en: '',
                },
                lastName: {
                    en: '',
                },
            },
            street: {
                en: '',
            },
            postalCode: null,
            phones: '',
            emails: null,
            comment: {
                en: '',
            },
            language: 'en',
        }
        cities = []

        mounted() {
            if (!this.companyInfoMessage) return
            // TODO [P3] Need implement localizable
            const {
                name,
                type,
                countryId,
                cityId,
                contactPerson: {firstName, lastName, prefix},
                address,
                postalCode,
                phones,
                emails,
                comments,
            } = this.companyInfoMessage
            this.model = {
                type: type.includes('SUPPLIER') ? 'INTERNAL_SUPPLIER' : 'TOUR_AGENCY',
                name: {
                    en: name,
                },
                countryId,
                cityId,
                contactPerson: {
                    prefix,
                    firstName: {
                        en: firstName,
                    },
                    lastName: {
                        en: lastName,
                    },
                },
                street: {
                    en: address,
                },
                postalCode,
                phones: phones?.[0],
                emails: emails?.[0],
                // TODO [P1] comment !== comments ???
                comment: {
                    en: comments?.en,
                },
                language: 'en',
            }
            console.log('!~!!!')
            this.loadCities()
        }

        get companyInfoMessage() {
            return import.meta.client && history?.state?.companyInfoMessage
        }

        get pageTitle() {
            return this.companyInfoMessage ? this.companyInfoMessage.name : this.$t(`page_title.${this.$route.name}`)
        }

        get countries() {
            return runtimeStore.countries.map(({id, name}) => {
                return {
                    name,
                    value: id,
                }
            })
        }

        get defaultCountryId() {
            // Greece fallback resolved from runtime countries by ISO2/name
            const list = runtimeStore.countries
            return (
                list.find(c => c.ISO2Code === 'GR')?.id ??
                list.find(c => c.name?.toLowerCase() === 'greece')?.id ??
                null
            )
        }

        async loadCities() {
            this.citiesLoading = true

            try {
                const rs = await this.$api.locations.get({
                    limitCities: -1,
                    orderBy: 'ALPHABET',
                    countryId: this.model.countryId || this.defaultCountryId,
                })

                this.cities = rs.cities

                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.citiesLoading = false
            }
        }

        async register() {
            this.isLoading = true
            let rq = {...this.model}
            rq.emails = Array.isArray(rq.emails) ? rq.emails : [rq.emails]
            rq.phones = Array.isArray(rq.phones) ? rq.phones : [rq.phones]
            if (!rq.phones.length) {
                delete rq.phones
            }
            if (!rq.postalCode) {
                delete rq.postalCode
            }
            clean(rq)
            if (!rq.cityId) {
                rq.cityId = 0
            }
            try {
                await this.$api.companyRegistration.post(rq)
                this.result = true
            } catch (e) {
                if (e.errors && e.errors.length && e.errors[0].token) {
                    const token = e.errors[0].token.replace(/\./g, '_')
                    if (token === 'api_validation_error_in_field') {
                        const validationMessage = e.errors[0]?.parameters?.[0]
                        this.$toast.error(validationMessage || this.$t(`api_error_token.${token}`))
                    } else this.$toast.error(this.$t('error_message.registration_error'))
                } else {
                    this.$toast.error(this.$t('error_message.registration_error'))
                }
            }
            this.isLoading = false
        }
    }

    export default CompanyRegistrationPage
</script>

<style scoped lang="scss">
    :deep(.v-input--radio-group.v-input--radio-group--row .v-radio) {
        min-height: 35px;
    }
</style>
